import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { hostOf } from './map-place'

const STEP_MS = [2500, 4000, 5000]

function steps(): string[] {
  return [
    m.placeWebStepFetch(),
    m.placeWebStepPages(),
    m.placeWebStepPhotos(),
    m.placeWebStepCompose(),
  ]
}

export function WorkingStep({ url }: { url: string }) {
  const labels = steps()
  const [active, setActive] = useState(0)

  useEffect(() => {
    let elapsed = 0
    const timers = STEP_MS.map((duration, index) => {
      elapsed += duration
      return window.setTimeout(() => setActive(index + 1), elapsed)
    })
    return () => timers.forEach(clearTimeout)
  }, [])

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="mx-auto flex w-full max-w-md flex-col items-center gap-6 pt-10 text-center sm:pt-16"
    >
      <PulsifyLoader className="h-24 w-auto" />
      <div className="flex flex-col gap-1.5">
        <p className="font-medium text-brand-subtle-foreground text-xs uppercase tracking-[0.18em]">
          {m.placeWebWorkingEyebrow({ host: hostOf(url) })}
        </p>
        <h1 className="font-semibold font-serif text-3xl text-foreground leading-[1.1] tracking-[-0.02em]">
          {m.placeWebWorkingTitle()}
        </h1>
      </div>
      <ol className="flex w-full flex-col gap-2.5 text-left">
        {labels.map((label, index) => {
          const state = index < active ? 'done' : index === active ? 'active' : 'upcoming'
          return (
            <li
              key={label}
              className={cn(
                'flex items-center gap-3 text-sm transition-colors duration-300',
                state === 'upcoming' ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              <span
                className={cn(
                  'relative flex size-6 shrink-0 items-center justify-center rounded-full border transition-colors duration-300',
                  state === 'upcoming'
                    ? 'border-border bg-card'
                    : 'border-transparent bg-brand text-brand-foreground',
                )}
              >
                {state === 'active' && (
                  <span className="absolute inset-0 rounded-full bg-brand/40 motion-safe:animate-ping" />
                )}
                {state === 'done' ? (
                  <Check className="relative size-3.5" />
                ) : (
                  <span
                    className={cn(
                      'relative size-1.5 rounded-full',
                      state === 'active' ? 'bg-brand-foreground' : 'bg-border',
                    )}
                  />
                )}
              </span>
              {label}
            </li>
          )
        })}
      </ol>
    </div>
  )
}
